import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from './config';
import './styles/WasteTickets.css';

interface WasteTicketsProps {
    user: { user_id: number; job_role: string };
}

const WasteTickets: React.FC<WasteTicketsProps> = ({ user }) => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [checkingSession, setCheckingSession] = useState(true);
    const [showUploadDialog, setShowUploadDialog] = useState<{ ticketId: string, type: 'pickup' | 'dropoff' } | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [images, setImages] = useState<{ [ticketId: string]: any[] }>({});
    const [previewImg, setPreviewImg] = useState<string | null>(null);
    const [coords, setCoords] = useState<{ [ticketId: string]: { site: [number, number], dropoff: [number, number] } }>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [siteOptions, setSiteOptions] = useState<string[]>([]);
    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [showSiteDropdown, setShowSiteDropdown] = useState(false);
    const TICKETS_PER_PAGE = 20;
    const [ticketPage, setTicketPage] = useState(1);
    const API_BASE = API_CONFIG.BASE_URL || 'http://localhost:5000';


    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        // First ensure session is valid, then load tickets
        axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(() => axios.get('/api/waste-tickets', { withCredentials: true, baseURL: API_CONFIG.BASE_URL }))
            .then(res => {
                if (cancelled) return;
                setTickets(res.data);
                const sites = Array.from(new Set(res.data.map((t: any) => String(t.site_label)))).filter(Boolean);
                setSiteOptions(sites as string[]);
            })
            .catch(err => {
                if (cancelled) return;
                // if session invalid -> mark expired and redirect to login
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else {
                    setTickets([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setCheckingSession(false);
                }
            });

        return () => { cancelled = true; };
    }, [navigate]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const dropdown = document.querySelector('.site-dropdown-container');
            if (dropdown && !dropdown.contains(event.target as Node)) {
                setShowSiteDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const columns = [
        "Select",
        "Ticket ID", "Site", "Waste Description", "Weight (Tonnes) ", "Waste Storage",
        "Vehicle Reg", "Driver Name", "Date", "Site Rep Name", "Site Rep Signature",
        "Site Rep Signature Timestamp", "Dropoff Site", "Dropoff Site Rep Name", "Dropoff Site Rep Signature",
        "Dropoff Site Rep Signature Timestamp", "Driver Signature", "Driver Signature Timestamp"
    ];

    const fetchImages = async (ticketId: string) => {
        try {
            const res = await axios.get(`/api/waste-ticket-images/${ticketId}`, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setImages(prev => ({ ...prev, [ticketId]: res.data }));
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
            }
        }
    };

    const handleUpload = async () => {
        if (!showUploadDialog || selectedFiles.length === 0) return;
        try {
            for (const file of selectedFiles) {
                const formData = new FormData();
                formData.append('waste_ticket_id', showUploadDialog.ticketId);
                formData.append('image_type', showUploadDialog.type);
                formData.append('uploaded_by', String(user.user_id));
                formData.append('image', file);
                await axios.post('/api/waste-ticket-image', formData, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            }
            setShowUploadDialog(null);
            setSelectedFiles([]);
            await fetchImages(showUploadDialog.ticketId);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
            } else {
                // optionally show upload error to user
                console.error(err);
            }
        }
    };

    // Helper: Check if images exist for a ticket and type
    const hasImages = (ticketId: string, type: 'pickup' | 'dropoff') =>
        (images[ticketId]?.some((img: any) => img.image_type === type));

    // Only allow upload if driver and no images exist yet for that type/ticket
    const canUploadPickup = (ticketId: string) =>
        user.job_role === 'Driver' && !hasImages(ticketId, 'pickup');
    const canUploadDropoff = (ticketId: string) =>
        user.job_role === 'Driver' && !hasImages(ticketId, 'dropoff');

    // Drivers can view images only after uploading for that type/ticket
    const canViewPickup = (ticketId: string) =>
        user.job_role !== 'Driver' || hasImages(ticketId, 'pickup');
    const canViewDropoff = (ticketId: string) =>
        user.job_role !== 'Driver' || hasImages(ticketId, 'dropoff');

    const filteredTickets = tickets.filter(ticket => {
        // Filter by sites if any selected
        if (selectedSites.length > 0 && !selectedSites.includes(ticket.site_label)) return false;
        // Search by ticket ID, driver name, vehicle reg, etc.
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                (ticket.waste_ticket_id && ticket.waste_ticket_id.toLowerCase().includes(term)) ||
                (ticket.driver_name && ticket.driver_name.toLowerCase().includes(term)) ||
                (ticket.vehicle_reg && ticket.vehicle_reg.toLowerCase().includes(term)) ||
                (ticket.site_label && ticket.site_label.toLowerCase().includes(term)) ||
                (ticket.waste_description && ticket.waste_description.toLowerCase().includes(term))
            );
        }
        return true;
    });

    const handleSiteToggle = (site: string) => {
        setSelectedSites(prev => 
            prev.includes(site)
                ? prev.filter(s => s !== site)
                : [...prev, site]
        );
    };

    const handleSelectAllSites = () => {
        if (selectedSites.length === siteOptions.length) {
            setSelectedSites([]);
        } else {
            setSelectedSites([...siteOptions]);
        }
    };

    const clearAllSites = () => {
        setSelectedSites([]);
    };

    function haversineDistance([lat1, lon1]: [number, number], [lat2, lon2]: [number, number]) {
        function toRad(x: number) { return x * Math.PI / 180; }
        const R = 3958.8; // Radius of Earth in miles
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    useEffect(() => {
        async function fetchCoordsForTickets() {
            const newCoords: typeof coords = {};
            for (const ticket of tickets) {
                const siteRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(ticket.site_address)}`);
                const siteData = await siteRes.json();
                const dropoffRes = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(ticket.dropoff_address)}`);
                const dropoffData = await dropoffRes.json();
                newCoords[ticket.waste_ticket_id] = {
                    site: siteData[0] ? [parseFloat(siteData[0].lat), parseFloat(siteData[0].lon)] : [0, 0],
                    dropoff: dropoffData[0] ? [parseFloat(dropoffData[0].lat), parseFloat(dropoffData[0].lon)] : [0, 0]
                };
            }
            setCoords(newCoords);
        }
        if (tickets.length > 0) fetchCoordsForTickets();
    }, [tickets]);

    // PDF Export Handler
    const handleExportPDF = async () => {
        if (selectedTicketIds.length === 0 || exportingPdf) return;
        
        setExportingPdf(true);
        try {
            const res = await axios.post('/api/export-waste-tickets-pdf', 
                { ticket_ids: selectedTicketIds }, 
                { 
                    responseType: 'blob', 
                    withCredentials: true, 
                    baseURL: API_CONFIG.BASE_URL,
                    timeout: 30000  // 30 second timeout
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 
                selectedTicketIds.length === 1 
                    ? `${selectedTicketIds[0]}.pdf` 
                    : 'waste_tickets.pdf'
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
            } else {
                console.error('PDF export error:', err);
                alert('Failed to export PDF. Please try again.');
            }
        } finally {
            setExportingPdf(false);
        }
    };

    const handleExportExcel = async () => {
        if (selectedTicketIds.length === 0 || exportingExcel) return;
        
        setExportingExcel(true);
        try {
            const res = await axios.post('/api/export-waste-tickets-excel', 
                { ticket_ids: selectedTicketIds }, 
                { 
                    responseType: 'blob', 
                    withCredentials: true, 
                    baseURL: API_CONFIG.BASE_URL,
                    timeout: 30000  // 30 second timeout
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([res.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 
                selectedTicketIds.length === 1 
                    ? `${selectedTicketIds[0]}_export.xlsx` 
                    : 'waste_tickets_export.xlsx'
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
            } else {
                console.error('Excel export error:', err);
                alert('Failed to export Excel file. Please try again.');
            }
        } finally {
            setExportingExcel(false);
        }
    };

    // Checkbox logic
    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedTicketIds(filteredTickets.map(t => t.waste_ticket_id));
        } else {
            setSelectedTicketIds([]);
        }
    };

    const handleSelectTicket = (ticketId: string, checked: boolean) => {
        if (checked) {
            setSelectedTicketIds(prev => [...prev, ticketId]);
        } else {
            setSelectedTicketIds(prev => prev.filter(id => id !== ticketId));
        }
    };

    const paginatedTickets = filteredTickets.slice(
        (ticketPage - 1) * TICKETS_PER_PAGE,
        ticketPage * TICKETS_PER_PAGE
    );

    return (
    <div
        style={{
            width: '90%',
            maxWidth: '80vw',
            minHeight: '100vh',
            background: '#f5f6fa',
            display: 'flex',
            flexDirection: 'column',
            padding: '40px', // Default desktop padding
            boxSizing: 'border-box',
            overflowX: 'hidden',
            margin: '0 auto'
        }}
        className="main-content-wrapper" // Add this class for responsive padding
    >
       
        <div className="waste-tickets-main-container">
            {/* Your existing content stays exactly the same */}
            <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>Waste Tickets</h2>
            
            {/* Responsive Controls */}
            <div className="controls-container">
                <div className="controls-row-1">
                    <input
                        type="text"
                        placeholder="Search by Ticket ID, Driver, Vehicle, Site etc"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    
                    {/* Multi-Select Site Dropdown */}
                    <div className="site-dropdown-container">
                        <button
                            type="button"
                            onClick={() => setShowSiteDropdown(!showSiteDropdown)}
                            className="site-dropdown-button"
                            style={{
                                border: selectedSites.length > 0 ? '2px solid #115740' : '1px solid #ccc',
                                background: selectedSites.length > 0 ? '#f0f8f0' : 'white'
                            }}
                        >
                            <span>
                                {selectedSites.length === 0 
                                    ? 'All Sites' 
                                    : selectedSites.length === 1 
                                        ? selectedSites[0]
                                        : `${selectedSites.length} Sites Selected`
                                }
                            </span>
                            <span style={{ marginLeft: 8 }}>
                                {showSiteDropdown ? '▲' : '▼'}
                            </span>
                        </button>
                        
                        {showSiteDropdown && (
                            <div className="site-dropdown-content">
                                <div className="site-dropdown-header">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedSites.length === siteOptions.length}
                                            onChange={handleSelectAllSites}
                                        />
                                        Select All Sites
                                    </label>
                                    {selectedSites.length > 0 && (
                                        <button
                                            onClick={clearAllSites}
                                            className="clear-sites-button"
                                        >
                                            Clear All
                                        </button>
                                    )}
                                </div>
                                
                                {siteOptions.map(site => (
                                    <div
                                        key={site}
                                        className="site-dropdown-item"
                                        onClick={() => handleSiteToggle(site)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSites.includes(site)}
                                            onChange={() => {}} // Handled by parent onClick
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        <span>{site}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="controls-row-2">
                    <button
                        disabled={selectedTicketIds.length === 0 || exportingPdf}
                        onClick={handleExportPDF}
                        className="export-button"
                        style={{
                            background: exportingPdf ? '#ccc' : '#115740',
                            color: '#fff',
                            cursor: (selectedTicketIds.length > 0 && !exportingPdf) ? 'pointer' : 'not-allowed',
                            opacity: (selectedTicketIds.length === 0 || exportingPdf) ? 0.6 : 1
                        }}
                    >
                        {exportingPdf ? 'Generating PDF...' : 'Export Selected as PDF'}
                    </button>
                    <button
                        disabled={selectedTicketIds.length === 0 || exportingExcel}
                        onClick={handleExportExcel}
                        className="export-button"
                        style={{
                            background: exportingExcel ? '#ccc' : '#F8BB00',
                            color: '#000',
                            cursor: (selectedTicketIds.length > 0 && !exportingExcel) ? 'pointer' : 'not-allowed',
                            opacity: (selectedTicketIds.length === 0 || exportingExcel) ? 0.6 : 1
                        }}
                    >
                        {exportingExcel ? 'Generating Excel...' : 'Export Selected to Excel'}
                    </button>
                </div>
            </div>

                {/* Table Section */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
                ) : (
                    <div 
                        className="table-container"
                        style={{
                            width: '100%',
                            maxHeight: '70vh',
                            overflow: 'auto',
                            borderRadius: '8px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                            background: '#fff',
                            margin: '20px 0 0 0', /* Remove auto margins */
                            display: 'block' /* Ensure block display */
                        }}
                    >
                        <table 
                            className="waste-table"
                            style={{
                                width: '100%',
                                minWidth: 'max-content',
                                borderCollapse: 'separate',
                                borderSpacing: '0 12px',
                                tableLayout: 'auto',
                                margin: 0 /* No margins on table */
                            }}
                        >
                            <thead>
                                <tr style={{ background: '#f4f4f4' }}>
                                    <th style={{
                                        minWidth: '50px',
                                        width: '50px',
                                        textAlign: 'center',
                                        padding: '16px 12px',
                                        position: 'sticky',
                                        left: 0,
                                        background: '#f4f4f4',
                                        zIndex: 10
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedTicketIds.length === filteredTickets.length && filteredTickets.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    {columns.slice(1).map((header, idx) => (
                                        <th
                                            key={header}
                                            style={{
                                                minWidth: '160px',
                                                padding: '16px 24px',
                                                fontWeight: 700,
                                                fontSize: '1rem',
                                                borderBottom: '2px solid #e0e0e0',
                                                background: idx % 2 === 0 ? '#f4f4f4' : '#e9e9e9',
                                                textAlign: 'left',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedTickets.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} style={{ textAlign: 'center', padding: '32px', color: '#888' }}>
                                            No waste tickets found.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedTickets.map((ticket, idx) => (
                                        <tr
                                            key={ticket.waste_ticket_id}
                                            style={{
                                                background: idx % 2 === 0 ? '#fff' : '#f6f8fa',
                                                borderBottom: '2px solid #e0e0e0',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#f0f0f0')}
                                            onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f6f8fa')}
                                        >
                                            <td style={{ 
                                                minWidth: '50px',
                                                width: '50px',
                                                textAlign: 'center',
                                                padding: '16px 12px',
                                                borderRight: '1px solid #e0e0e0',
                                                position: 'sticky',
                                                left: 0,
                                                background: 'inherit',
                                                zIndex: 9
                                            }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTicketIds.includes(ticket.waste_ticket_id)}
                                                    onChange={e => handleSelectTicket(ticket.waste_ticket_id, e.target.checked)}
                                                />
                                            </td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.waste_ticket_id}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.site_label}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.waste_description}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.weight}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.waste_storage}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.vehicle_reg}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.driver_name}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.date}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.site_rep_name}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>
                                                {ticket.signature
                                                    ? <img
                                                        src={`${API_BASE}${ticket.signature}`}
                                                        alt="Site Rep Signature"
                                                        style={{ maxWidth: 60, maxHeight: 40, borderRadius: 4, cursor: 'pointer', background: '#eee' }}
                                                        loading="lazy"
                                                        onClick={() => setPreviewImg(`${API_BASE}${ticket.signature}`)}
                                                        onError={e => {
                                                            if (!e.currentTarget.dataset.placeholder) {
                                                                e.currentTarget.src = '/images/placeholder-signature.png';
                                                                e.currentTarget.dataset.placeholder = 'true';
                                                            }
                                                        }}
                                                    />
                                                    : <span style={{ color: '#888' }}>—</span>
                                                }
                                            </td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.signature_timestamp}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0' }}>{ticket.selected_dropoff_site}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.dropoff_site_rep_name}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>
                                                {ticket.dropoff_site_rep_signature
                                                    ? <img
                                                        src={`${API_BASE}${ticket.dropoff_site_rep_signature}`}
                                                        alt="Dropoff Site Rep Signature"
                                                        style={{ maxWidth: 60, maxHeight: 40, borderRadius: 4, cursor: 'pointer', background: '#eee' }}
                                                        loading="lazy"
                                                        onClick={() => setPreviewImg(`${API_BASE}${ticket.dropoff_site_rep_signature}`)}
                                                        onError={e => {
                                                            if (!e.currentTarget.dataset.placeholder) {
                                                                e.currentTarget.src = '/images/placeholder-signature.png';
                                                                e.currentTarget.dataset.placeholder = 'true';
                                                            }
                                                        }}
                                                    />
                                                    : <span style={{ color: '#888' }}>—</span>
                                                }
                                            </td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.dropoff_site_rep_signature_timestamp}</td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>
                                                {ticket.driver_signature
                                                    ? <img
                                                        src={`${API_BASE}${ticket.driver_signature}`}
                                                        alt="Driver Signature"
                                                        style={{ maxWidth: 60, maxHeight: 40, borderRadius: 4, cursor: 'pointer', background: '#eee' }}
                                                        loading="lazy"
                                                        onClick={() => setPreviewImg(`${API_BASE}${ticket.driver_signature}`)}
                                                        onError={e => {
                                                            if (!e.currentTarget.dataset.placeholder) {
                                                                e.currentTarget.src = '/images/placeholder-signature.png';
                                                                e.currentTarget.dataset.placeholder = 'true';
                                                            }
                                                        }}
                                                    />
                                                    : <span style={{ color: '#888' }}>—</span>
                                                }
                                            </td>
                                            <td style={{ minWidth: '160px', padding: '16px 24px', borderRight: '1px solid #e0e0e0', background: '#f9f9f9' }}>{ticket.driver_signature_timestamp}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Keep your existing modals exactly as they are */}
                {showUploadDialog && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, width: '100vw', height: '100vh',
                        background: 'rgba(0,0,0,0.18)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 1000
                    }}>
                        <div style={{
                            background: '#fff', padding: '32px', borderRadius: '12px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.12)', minWidth: 320
                        }}>
                            <h3>Upload {showUploadDialog.type === 'pickup' ? 'Waste on Site' : 'Waste at Dropoff'} Images</h3>
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={e => setSelectedFiles(e.target.files ? Array.from(e.target.files) : [])}
                                style={{ marginBottom: 16 }}
                            />
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button
                                    onClick={handleUpload}
                                    disabled={selectedFiles.length === 0}
                                    style={{
                                        background: '#115740',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '8px 18px',
                                        fontWeight: 600,
                                        cursor: selectedFiles.length > 0 ? 'pointer' : 'not-allowed',
                                        boxShadow: '0 2px 8px rgba(17,87,64,0.08)'
                                    }}
                                >
                                    Upload Selected Images
                                </button>
                                <button
                                    onClick={() => { setShowUploadDialog(null); setSelectedFiles([]); }}
                                    style={{
                                        background: '#eee',
                                        color: '#333',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '8px 18px',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                            {selectedFiles.length > 0 && (
                                <div style={{ marginTop: 12 }}>
                                    <strong>Selected files:</strong>
                                    <ul>
                                        {selectedFiles.map((file, idx) => (
                                            <li key={idx}>{file.name}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {previewImg && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, width: '100vw', height: '100vh',
                        background: 'rgba(0,0,0,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 2000
                    }}>
                        <div style={{
                            background: '#fff', padding: 24, borderRadius: 12,
                            boxShadow: '0 2px 12px rgba(0,0,0,0.18)', position: 'relative'
                        }}>
                            <img src={previewImg} alt="Preview" style={{ maxWidth: '80vw', maxHeight: '80vh', borderRadius: 8 }} />
                            <button
                                onClick={() => setPreviewImg(null)}
                                style={{
                                    position: 'absolute', top: 12, right: 12,
                                    background: '#1976d2', color: '#fff', border: 'none',
                                    borderRadius: 6, padding: '6px 14px', fontWeight: 600, cursor: 'pointer'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}

                {filteredTickets.length > TICKETS_PER_PAGE && (
                    <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'center', gap: 12 }}>
                        <button
                            disabled={ticketPage === 1}
                            onClick={() => setTicketPage(ticketPage - 1)}
                            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
                        >Prev</button>
                        <span>Page {ticketPage} of {Math.ceil(filteredTickets.length / TICKETS_PER_PAGE)}</span>
                        <button
                            disabled={ticketPage === Math.ceil(filteredTickets.length / TICKETS_PER_PAGE)}
                            onClick={() => setTicketPage(ticketPage + 1)}
                            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}
                        >Next</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WasteTickets;